import { toast } from "sonner";

export const SHOPIFY_API_VERSION = "2025-07";
export const SHOPIFY_STORE_PERMANENT_DOMAIN = "hawmyf-0h.myshopify.com";
export const SHOPIFY_STOREFRONT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`;
export const SHOPIFY_STOREFRONT_TOKEN = "03330bbf53dddbb5da9278aa1f04db66";

export interface ShopifyProduct {
  node: {
    id: string;
    title: string;
    description: string;
    handle: string;
    priceRange: {
      minVariantPrice: {
        amount: string;
        currencyCode: string;
      };
    };
    images: {
      edges: Array<{
        node: {
          url: string;
          altText: string | null;
        };
      }>;
    };
    variants: {
      edges: Array<{
        node: {
          id: string;
          title: string;
          availableForSale: boolean;
          price: {
            amount: string;
            currencyCode: string;
          };
          compareAtPrice?: {
            amount: string;
            currencyCode: string;
          } | null;
          selectedOptions: Array<{
            name: string;
            value: string;
          }>;
        };
      }>;
    };
    options: Array<{
      name: string;
      values: string[];
    }>;
  };
}

type StorefrontResponse<T> = {
  data?: T;
  errors?: Array<{ message: string }>;
};

type ShopifyUserError = {
  field: string[] | null;
  message: string;
};

const PRODUCT_FIELDS = `
  id
  title
  description
  handle
  priceRange {
    minVariantPrice {
      amount
      currencyCode
    }
  }
  images(first: 5) {
    edges {
      node {
        url
        altText
      }
    }
  }
  variants(first: 10) {
    edges {
      node {
        id
        title
        availableForSale
        price {
          amount
          currencyCode
        }
        compareAtPrice {
          amount
          currencyCode
        }
        selectedOptions {
          name
          value
        }
      }
    }
  }
  options {
    name
    values
  }
`;

const RESOURCE_PRODUCTS_QUERY = `
  query GetProducts($first: Int!, $query: String) {
    products(first: $first, query: $query) {
      edges {
        node {
          ${PRODUCT_FIELDS}
        }
      }
    }
  }
`;

const PRODUCT_BY_HANDLE_QUERY = `
  query GetProductByHandle($handle: String!) {
    product(handle: $handle) {
      ${PRODUCT_FIELDS}
    }
  }
`;

const CART_QUERY = `
  query cart($id: ID!) {
    cart(id: $id) {
      id
      totalQuantity
      checkoutUrl
    }
  }
`;

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        lines(first: 100) {
          edges {
            node {
              id
              merchandise {
                ... on ProductVariant {
                  id
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const CART_LINES_ADD_MUTATION = `
  mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
    cartLinesAdd(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
        lines(first: 100) {
          edges {
            node {
              id
              merchandise {
                ... on ProductVariant {
                  id
                }
              }
            }
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const CART_LINES_UPDATE_MUTATION = `
  mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart {
        id
        checkoutUrl
      }
      userErrors {
        field
        message
      }
    }
  }
`;

const CART_LINES_REMOVE_MUTATION = `
  mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
    cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
      cart {
        id
        checkoutUrl
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export async function storefrontApiRequest<T>(query: string, variables: Record<string, unknown> = {}) {
  const response = await fetch(SHOPIFY_STOREFRONT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (response.status === 402) {
    toast.error("Shopify requiere un plan activo", {
      description: "La API de storefront necesita que la tienda tenga billing activo para responder.",
    });
    return null;
  }

  if (!response.ok) {
    throw new Error(`Error HTTP de Shopify: ${response.status}`);
  }

  const data = (await response.json()) as StorefrontResponse<T>;

  if (data.errors?.length) {
    throw new Error(data.errors.map((error) => error.message).join(", "));
  }

  return data;
}

export async function fetchResourceProducts() {
  const data = await storefrontApiRequest<{ products: { edges: ShopifyProduct[] } }>(RESOURCE_PRODUCTS_QUERY, {
    first: 12,
    query: "tag:recursos-digitales",
  });

  return data?.data?.products.edges ?? [];
}

export async function fetchShopifyProductByHandle(handle: string) {
  const data = await storefrontApiRequest<{ product: ShopifyProduct["node"] | null }>(PRODUCT_BY_HANDLE_QUERY, {
    handle,
  });

  const product = data?.data?.product;
  return product ? { node: product } : null;
}

function formatCheckoutUrl(checkoutUrl: string) {
  try {
    const url = new URL(checkoutUrl);
    url.searchParams.set("channel", "online_store");
    return url.toString();
  } catch {
    return checkoutUrl;
  }
}

function isCartNotFoundError(userErrors: ShopifyUserError[]) {
  return userErrors.some(
    (error) =>
      error.message.toLowerCase().includes("cart not found") ||
      error.message.toLowerCase().includes("does not exist"),
  );
}

export async function fetchShopifyCart(cartId: string) {
  const data = await storefrontApiRequest<{
    cart: {
      id: string;
      totalQuantity: number;
      checkoutUrl: string;
    } | null;
  }>(CART_QUERY, { id: cartId });

  const cart = data?.data?.cart;
  if (!cart) return null;

  return {
    id: cart.id,
    totalQuantity: cart.totalQuantity,
    checkoutUrl: formatCheckoutUrl(cart.checkoutUrl),
  };
}

export async function createShopifyCart(variantId: string, quantity: number) {
  const data = await storefrontApiRequest<{
    cartCreate: {
      cart: {
        id: string;
        checkoutUrl: string;
        lines: {
          edges: Array<{
            node: {
              id: string;
              merchandise: { id: string };
            };
          }>;
        };
      } | null;
      userErrors: ShopifyUserError[];
    };
  }>(CART_CREATE_MUTATION, {
    input: {
      lines: [{ quantity, merchandiseId: variantId }],
    },
  });

  const payload = data?.data?.cartCreate;
  if (!payload || payload.userErrors.length > 0 || !payload.cart) {
    return null;
  }

  const lineId = payload.cart.lines.edges[0]?.node.id;
  if (!lineId) return null;

  return {
    cartId: payload.cart.id,
    checkoutUrl: formatCheckoutUrl(payload.cart.checkoutUrl),
    lineId,
  };
}

export async function addLineToShopifyCart(cartId: string, variantId: string, quantity: number) {
  const data = await storefrontApiRequest<{
    cartLinesAdd: {
      cart: {
        id: string;
        checkoutUrl: string;
        lines: {
          edges: Array<{
            node: {
              id: string;
              merchandise: { id: string };
            };
          }>;
        };
      } | null;
      userErrors: ShopifyUserError[];
    };
  }>(CART_LINES_ADD_MUTATION, {
    cartId,
    lines: [{ quantity, merchandiseId: variantId }],
  });

  const payload = data?.data?.cartLinesAdd;
  const userErrors = payload?.userErrors ?? [];

  if (isCartNotFoundError(userErrors)) {
    return { success: false, cartNotFound: true, checkoutUrl: null as string | null, lineId: null as string | null };
  }

  if (!payload?.cart || userErrors.length > 0) {
    return { success: false, cartNotFound: false, checkoutUrl: null as string | null, lineId: null as string | null };
  }

  const line = payload.cart.lines.edges.find((entry) => entry.node.merchandise.id === variantId);

  return {
    success: true,
    cartNotFound: false,
    checkoutUrl: formatCheckoutUrl(payload.cart.checkoutUrl),
    lineId: line?.node.id ?? null,
  };
}

export async function updateShopifyCartLine(cartId: string, lineId: string, quantity: number) {
  const data = await storefrontApiRequest<{
    cartLinesUpdate: {
      cart: {
        id: string;
        checkoutUrl: string;
      } | null;
      userErrors: ShopifyUserError[];
    };
  }>(CART_LINES_UPDATE_MUTATION, {
    cartId,
    lines: [{ id: lineId, quantity }],
  });

  const payload = data?.data?.cartLinesUpdate;
  const userErrors = payload?.userErrors ?? [];

  if (isCartNotFoundError(userErrors)) {
    return { success: false, cartNotFound: true, checkoutUrl: null as string | null };
  }

  if (!payload?.cart || userErrors.length > 0) {
    return { success: false, cartNotFound: false, checkoutUrl: null as string | null };
  }

  return {
    success: true,
    cartNotFound: false,
    checkoutUrl: formatCheckoutUrl(payload.cart.checkoutUrl),
  };
}

export async function removeLineFromShopifyCart(cartId: string, lineId: string) {
  const data = await storefrontApiRequest<{
    cartLinesRemove: {
      cart: {
        id: string;
        checkoutUrl: string;
      } | null;
      userErrors: ShopifyUserError[];
    };
  }>(CART_LINES_REMOVE_MUTATION, {
    cartId,
    lineIds: [lineId],
  });

  const payload = data?.data?.cartLinesRemove;
  const userErrors = payload?.userErrors ?? [];

  if (isCartNotFoundError(userErrors)) {
    return { success: false, cartNotFound: true, checkoutUrl: null as string | null };
  }

  if (userErrors.length > 0) {
    return { success: false, cartNotFound: false, checkoutUrl: null as string | null };
  }

  return {
    success: true,
    cartNotFound: false,
    checkoutUrl: payload?.cart ? formatCheckoutUrl(payload.cart.checkoutUrl) : null,
  };
}
